import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, DollarSign, Calendar, FileText, Link as LinkIcon, ExternalLink, Mail, Phone, Download, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const StaffDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchStaff();
  }, [id]);

  const fetchStaff = async () => {
    try {
      const response = await api.get(`/staff/${id}`);
      setMember(response.data);
    } catch (error) {
      toast.error('Failed to load staff details');
      navigate('/staff');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;
    setDeleting(true);
    try {
      await api.delete(`/staff/${id}`);
      toast.success('Staff member deleted successfully');
      navigate('/staff');
    } catch (error) {
      toast.error('Failed to delete staff member');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <Link to="/staff" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors duration-200">
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            <span className="uppercase tracking-wide text-sm">Back to Coaches</span>
          </Link>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex items-start gap-6">
              {member.photo ? (
                <img src={member.photo} alt={member.name} className="w-24 h-24 md:w-32 md:h-32 rounded-lg object-cover border-2 border-white/10 flex-shrink-0" />
              ) : (
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-lg bg-zinc-800 border-2 border-white/10 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-12 h-12 text-zinc-600" />
                </div>
              )}
              <div>
                <h1 data-testid="staff-detail-name" className="text-4xl md:text-5xl font-bold uppercase tracking-tight leading-none text-white mb-2">
                  {member.name}
                </h1>
                <p className="text-lg text-zinc-300">{member.department || ''}</p>
                {member.current_club && (
                  <span className="inline-block mt-2 px-3 py-1 bg-white/20 text-white text-sm font-bold uppercase rounded">
                    {member.current_club}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <Link to={`/staff/${id}/edit`} data-testid="edit-staff-button"
                className="inline-flex items-center gap-2 bg-white/10 text-white hover:bg-white/20 border border-white/5 rounded-sm px-4 py-2 transition-all duration-200"
              >
                <Edit className="w-4 h-4" strokeWidth={1.5} />
                Edit
              </Link>
              <button onClick={handleDelete} disabled={deleting} data-testid="delete-staff-button"
                className="inline-flex items-center gap-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 rounded-sm px-4 py-2 transition-all duration-200 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {member.notes && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4">Notes</h3>
                <p className="text-zinc-300 leading-relaxed">{member.notes}</p>
              </div>
            )}

            {member.links && member.links.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                  <LinkIcon className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                  Links
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {member.links.map((link, index) => (
                    <a key={index} href={link.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors group"
                    >
                      <LinkIcon className="w-4 h-4 text-blue-400" />
                      <span className="text-white font-medium flex-1">{link.name}</span>
                      <ExternalLink className="w-4 h-4 text-zinc-500 group-hover:text-blue-400" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {member.documents && member.documents.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-400" strokeWidth={1.5} />
                  Documents
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {member.documents.map((doc, index) => (
                    <button key={index} onClick={() => {
                      if (doc.data) {
                        const link = document.createElement('a');
                        link.href = doc.data;
                        link.download = doc.filename || `${doc.name}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      } else if (doc.url) {
                        window.open(doc.url, '_blank');
                      }
                    }}
                      className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors group text-left"
                    >
                      <FileText className="w-4 h-4 text-red-400" />
                      <span className="text-white font-medium flex-1">{doc.name}</span>
                      <Download className="w-4 h-4 text-zinc-500 group-hover:text-red-400" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {(member.email || member.phone) && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                  Contact
                </h3>
                <div className="space-y-3">
                  {member.email && (
                    <a href={`mailto:${member.email}`} className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
                      <Mail className="w-4 h-4 text-blue-400" />
                      <span className="text-white text-sm">{member.email}</span>
                    </a>
                  )}
                  {member.phone && (
                    <a href={`tel:${member.phone}`} className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
                      <Phone className="w-4 h-4 text-green-400" />
                      <span className="text-white text-sm">{member.phone}</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {member.salary && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" strokeWidth={1.5} />
                  Contract Details
                </h3>
                <div className="p-4 bg-white/5 rounded-lg">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Salary</p>
                  <p className="text-2xl font-bold tabular-nums text-green-400">
                    £{member.salary.toLocaleString()}
                    <span className="text-sm text-zinc-400 ml-1">/{member.salary_frequency || 'yearly'}</span>
                  </p>
                </div>
              </div>
            )}

            {member.contract_end_date && (
              <div className="glass-card p-6">
                <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" strokeWidth={1.5} />
                  Contract End Date
                </h3>
                <p className="text-lg font-bold text-white">{formatDate(member.contract_end_date)}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffDetail;
